import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import api from "../lib/api";

const TYPE_STYLE = {
  interview: "border-primary/40 bg-primary/10 text-primary",
  hire: "border-[#27AE60]/40 bg-[#27AE60]/10 text-[#1e8449]",
  birthday: "border-[#D68910]/40 bg-[#D68910]/10 text-[#9a6a0b]",
  time_off: "border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B]",
  training_due: "border-stone-400 bg-stone-100 text-stone-600",
  evv_dip: "border-[#C0392B]/40 bg-[#C0392B]/5 text-[#C0392B] border-dashed",
  client_birthday: "border-[#D68910]/40 bg-[#D68910]/10 text-[#9a6a0b] border-dashed",
  appointment: "border-[#27AE60]/40 bg-[#27AE60]/5 text-[#1e8449]",
  missed_visit: "border-[#C0392B] bg-[#C0392B]/15 text-[#C0392B]",
  checkin_due: "border-primary/60 bg-primary/15 text-primary",
  anniversary: "border-[#B7791F]/50 bg-[#B7791F]/10 text-[#8a5d14]",
};

const TYPE_LABEL = { interview: "Interview", hire: "Hire date", birthday: "Birthday", time_off: "Time off", training_due: "Training due", evv_dip: "EVV dip", client_birthday: "Client birthday", appointment: "Appointment", missed_visit: "Missed visit", checkin_due: "Check-in due", anniversary: "Work anniversary" };

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function CalendarPage() {
  const [searchParams] = useSearchParams();
  const focusDate = searchParams.get("focus") || "";
  const [month, setMonth] = useState(() => (focusDate.slice(0, 7) || new Date().toISOString().slice(0, 7)));
  const [data, setData] = useState(null);
  const [todo, setTodo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/calendar/events?month=${month}`).then((r) => setData(r.data)).catch(() => {});
  }, [month]);

  useEffect(() => {
    api.get("/admin/todo").then((r) => setTodo(r.data)).catch(() => {});
  }, []);

  const [y, m] = month.split("-").map(Number);
  const startDay = new Date(y, m - 1, 1).getDay();
  const daysInMonth = new Date(y, m, 0).getDate();
  const cells = [...Array(startDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of data?.events || []) {
      if (!e.date) continue;
      const dates = [e.date];
      if (e.end_date && e.end_date > e.date) {
        let d = new Date(`${e.date}T12:00:00`);
        const end = new Date(`${e.end_date}T12:00:00`);
        while (d < end && dates.length < 62) {
          d = new Date(d.getTime() + 86400000);
          const iso = d.toISOString().slice(0, 10);
          if (iso.startsWith(month)) dates.push(iso);
        }
      }
      for (const dd of dates) {
        if (!dd.startsWith(month)) continue;
        const day = Number(dd.slice(8, 10));
        (map[day] = map[day] || []).push(e);
      }
    }
    return map;
  }, [data, month]);

  const shift = (delta) => {
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(d.toISOString().slice(0, 7));
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="mx-auto max-w-5xl" data-testid="calendar-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Calendar</h1>
          <p className="mt-1 text-sm text-stone-500">Interviews, hire dates, birthdays, and time off</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-stone-300 text-stone-600 hover:bg-stone-900 hover:text-white" data-testid="cal-prev"><CaretLeft size={16} /></button>
          <p className="min-w-36 text-center font-heading text-lg font-semibold text-stone-900" data-testid="cal-month">{MONTHS[m - 1]} {y}</p>
          <button onClick={() => shift(1)} className="flex h-9 w-9 items-center justify-center rounded-full border border-stone-300 text-stone-600 hover:bg-stone-900 hover:text-white" data-testid="cal-next"><CaretRight size={16} /></button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2" data-testid="cal-legend">
        {Object.entries(TYPE_LABEL).map(([k, l]) => (
          <span key={k} className={`rounded-full border px-3 py-1 text-xs font-medium ${TYPE_STYLE[k]}`}>{l}</span>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_260px]">
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="cal-grid">
          <div className="grid grid-cols-7 border-b border-stone-200 bg-secondary/60 text-center text-[10px] font-semibold uppercase tracking-wide text-stone-500">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-2">{d}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {cells.map((d, i) => {
              const iso = d ? `${month}-${String(d).padStart(2, "0")}` : "";
              const evs = d ? (eventsByDay[d] || []) : [];
              return (
                <div key={i} className={`min-h-20 border-b border-r border-stone-100 p-1.5 ${iso === today ? "bg-primary/5" : ""} ${!d ? "bg-stone-50/50" : ""}`}
                  data-testid={d ? `cal-day-${d}` : undefined}>
                  {d && <p className={`text-xs font-medium ${iso === today ? "text-primary" : "text-stone-400"}`}>{d}</p>}
                  <div className="mt-0.5 space-y-0.5">
                    {evs.slice(0, 3).map((e, j) => (
                      <button key={j} onClick={() => e.ref && navigate(e.ref)}
                        className={`block w-full truncate rounded border px-1 py-0.5 text-left text-[10px] font-medium ${TYPE_STYLE[e.type] || "border-stone-200 text-stone-600"}`}
                        data-testid={`cal-event-${d}-${j}`}>
                        {e.time ? `${e.time} ` : ""}{e.label}
                      </button>
                    ))}
                    {evs.length > 3 && <p className="text-[10px] text-stone-400">+{evs.length - 3} more</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="todo-panel">
            <p className="font-heading text-sm font-semibold text-stone-900">To-do</p>
            <p className="mt-0.5 text-[10px] text-stone-400">Auto-clears as each item is fixed.</p>
            {!todo ? (
              <p className="mt-2 text-xs text-stone-400">Loading…</p>
            ) : todo.total === 0 ? (
              <p className="mt-2 text-xs text-[#1e8449]" data-testid="todo-empty">All clear — nothing needs attention.</p>
            ) : (
              <div className="mt-2 space-y-3">
                {[["callout", "Call-outs"], ["training_overdue", "Trainings overdue"], ["missed_visit", "Missed visits"], ["missed_punch", "Missed punches to sign off"], ["careplan_ack", "Care plan acknowledgments"], ["checkin_due", "Quarterly check-ins"], ["training_due", "Trainings due soon"], ["notifications", "Notifications to approve"]]
                  .filter(([k]) => todo.counts?.[k]).map(([k, label]) => (
                  <div key={k} data-testid={`todo-group-${k}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-stone-400">{label} ({todo.counts[k]})</p>
                    <div className="mt-1 space-y-1">
                      {todo.items.filter((i) => i.kind === k).slice(0, 6).map((i, j) => (
                        <button key={j} onClick={() => i.ref && navigate(i.ref)}
                          className="block w-full rounded-lg border border-stone-200 px-2.5 py-1.5 text-left text-xs text-stone-600 transition-colors hover:border-primary/40 hover:bg-primary/5"
                          data-testid={`todo-item-${k}-${j}`}>{i.label}</button>
                      ))}
                      {todo.counts[k] > 6 && <p className="text-[10px] text-stone-400">+{todo.counts[k] - 6} more</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="cal-roster-blocks">
            <p className="font-heading text-sm font-semibold text-stone-900">Pending roster blocks</p>
            {!data ? (
              <p className="mt-2 text-xs text-stone-400">Loading…</p>
            ) : (data.pending_roster_blocks || []).length === 0 ? (
              <p className="mt-2 text-xs text-[#1e8449]">No one is blocked from the roster right now.</p>
            ) : (
              <div className="mt-2 space-y-1.5">
                {data.pending_roster_blocks.map((b, i) => (
                  <button key={i} onClick={() => navigate(b.ref)} className="block w-full rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 px-3 py-2 text-left text-xs hover:bg-[#B7791F]/10" data-testid={`cal-block-${i}`}>
                    <span className="font-medium text-stone-800">{b.name}</span>
                    <span className="ml-1 text-stone-500">— {(b.status || "").replace(/_/g, " ")}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm" data-testid="cal-upcoming">
            <p className="font-heading text-sm font-semibold text-stone-900">This month</p>
            <div className="mt-2 space-y-1.5">
              {(data?.events || []).length === 0 && <p className="text-xs text-stone-400">Nothing scheduled this month.</p>}
              {(data?.events || []).slice().sort((a, b) => (a.date || "").localeCompare(b.date || "")).slice(0, 12).map((e, i) => (
                <button key={i} onClick={() => e.ref && navigate(e.ref)} className="block w-full text-left" data-testid={`cal-upcoming-${i}`}>
                  <p className="text-xs"><span className="font-medium text-stone-700">{e.date?.slice(5).replace("-", "/")}</span>{" "}
                    <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${TYPE_STYLE[e.type]}`}>{TYPE_LABEL[e.type]}</span>{" "}
                    <span className="text-stone-600">{e.label.replace(/^(Interview|Birthday|Hired|Time off) — /, "")}</span></p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
